import { saveAs } from 'file-saver';

const EXCEL_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const HEADER_FILL = '111111';
const HEADER_FONT = 'FFFFFF';
const SUMMARY_FILL = 'F3F4F6';

function collectColumnKeys(rows: Record<string, unknown>[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }

  return keys;
}

export type ExcelSheet = {
  name: string;
  rows: Record<string, unknown>[];
  /** Filas de resumen al final (sin incluirlas en la tabla filtrable). */
  summaryRows?: Record<string, unknown>[];
};

function isBlankRow(row: Record<string, unknown>): boolean {
  return Object.values(row).every(v => v === '' || v == null);
}

function splitDataAndSummary(rows: Record<string, unknown>[]): {
  data: Record<string, unknown>[];
  summary: Record<string, unknown>[];
} {
  const blankIdx = rows.findIndex(isBlankRow);
  if (blankIdx < 0) return { data: rows, summary: [] };
  return {
    data: rows.slice(0, blankIdx),
    summary: rows.slice(blankIdx + 1)
  };
}

async function addSheet(workbook: any, sheet: ExcelSheet): Promise<void> {
  const worksheet = workbook.addWorksheet(sheet.name.slice(0, 31) || 'data');
  const providedSummary = sheet.summaryRows || [];
  const { data, summary: embeddedSummary } = splitDataAndSummary(sheet.rows || []);
  const summary = providedSummary.length ? providedSummary : embeddedSummary;
  const columnKeys = collectColumnKeys([...data, ...summary]);

  if (!columnKeys.length) return;

  worksheet.columns = columnKeys.map((key: string) => ({
    header: key,
    key,
    width: Math.max(12, Math.min(28, key.length + 4))
  }));

  if (data.length) {
    // Tabla nativa de Excel: encabezado, filtros y filas (sin addRows previos para no duplicar)
    const tableName = `T_${sheet.name.replace(/[^A-Za-z0-9]/g, '_').slice(0, 20)}_${Math.floor(Math.random() * 9999)}`;
    worksheet.addTable({
      name: tableName,
      ref: 'A1',
      headerRow: true,
      totalsRow: false,
      style: {
        theme: 'TableStyleMedium2',
        showRowStripes: true
      },
      columns: columnKeys.map((key: string) => ({ name: key, filterButton: true })),
      rows: data.map(row => columnKeys.map(k => row[k] ?? ''))
    });

    const headerRow = worksheet.getRow(1);
    headerRow.height = 22;
    headerRow.eachCell((cell: any) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: HEADER_FILL }
      };
      cell.font = { bold: true, color: { argb: HEADER_FONT }, size: 11 };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    });
  } else {
    // Solo encabezados si no hay data
    const headerRow = worksheet.getRow(1);
    columnKeys.forEach((key, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = key;
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: HEADER_FILL }
      };
      cell.font = { bold: true, color: { argb: HEADER_FONT }, size: 11 };
    });
  }

  if (summary.length) {
    const start = (data.length ? data.length + 1 : 1) + 2; // fila vacía + resumen
    summary.forEach((row, idx) => {
      const excelRow = worksheet.getRow(start + idx);
      columnKeys.forEach((key, colIdx) => {
        const cell = excelRow.getCell(colIdx + 1);
        cell.value = (row[key] as any) ?? '';
        cell.font = { bold: true, size: 11 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: SUMMARY_FILL }
        };
      });
    });
  }

  worksheet.views = [{ state: 'frozen', ySplit: 1 }];
}

export async function exportJsonToExcel(
  rows: Record<string, unknown>[],
  fileName: string,
  sheetName = 'data'
): Promise<void> {
  return exportJsonSheetsToExcel([{ name: sheetName, rows }], fileName);
}

/** Un solo .xlsx con varias hojas (tabla + filtros + encabezado). */
export async function exportJsonSheetsToExcel(
  sheets: ExcelSheet[],
  fileName: string
): Promise<void> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const list = sheets?.length ? sheets : [{ name: 'data', rows: [] as Record<string, unknown>[] }];
  for (const sheet of list) {
    await addSheet(workbook, sheet);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: EXCEL_MIME });
  const downloadName = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  saveAs(blob, downloadName);
}
